// =============================================================================
// AGC: [CROP_PLAN] CROP PLAN SERVICE
// AGC: Service for managing crop plans and required inputs
// AGC: Handles generation, persistence, and retrieval of crop plans and inputs
// =============================================================================
// AGC: REFACTORED - No longer uses hardcoded cropInputRequirements.ts
// AGC: Task inputs now come from Admin Panel task templates via generateTasksForCrop()
// AGC: in lotServices.ts. This service is kept for BOM summary generation.
// =============================================================================

import { getBackendUrl } from '../utils/backendUrl'
import { calculateTotalQuantity } from '../types/unitConversion'
import type {
  CropPlan,
  CropPlanStatus,
  RequiredInput,
  RequiredInputsSummary,
  InputCategorySummary,
  GenerateCropPlanRequest,
  GenerateCropPlanResponse,
  GetRequiredInputsRequest,
  GetRequiredInputsResponse,
  InputTemplate,
  INPUT_CATEGORIES,
  getInputCategoryInfo,
} from '../types/cropPlan'
import type { CropType } from '../types/field'

// AGC: Backend URL for API calls
const BASE_URL = getBackendUrl()

// =============================================================================
// AGC: [CROP_PLAN] HELPER FUNCTIONS
// =============================================================================

/**
 * AGC: [CROP_PLAN] Generate unique ID
 */
const generateId = (prefix: string): string => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * AGC: [CROP_PLAN] Get ISO timestamp
 */
const getTimestamp = (): string => {
  return new Date().toISOString()
}

/**
 * AGC: [CROP_PLAN] Convert input template to required input
 */
const templateToRequiredInput = (
  template: InputTemplate,
  planId: string,
  lotId: string,
  lotAreaHa: number,
  order: number,
  taskId?: string
): RequiredInput => {
  const totalQuantity = calculateTotalQuantity(template.quantityPerHa, lotAreaHa)
  const now = getTimestamp()

  return {
    id: generateId('input'),
    planId,
    lotId,
    taskId,
    genericName: template.genericName,
    commercialReference: template.commercialReference,
    activeIngredient: template.activeIngredient,
    category: template.category,
    taskType: template.taskType,
    applicationPhase: template.applicationPhase,
    unit: template.unit,
    quantityPerHa: template.quantityPerHa,
    totalQuantity,
    lotAreaHa,
    applicationWindow: template.applicationWindow,
    growthStageRecommended: template.growthStageRecommended,
    notes: template.notes,
    order,
    isRequired: template.isRequired,
    createdAt: now,
    updatedAt: now,
  }
}

// =============================================================================
// AGC: [CROP_PLAN] LOCAL GENERATION FUNCTIONS
// =============================================================================

/**
 * AGC: [CROP_PLAN] Generate required inputs from crop type and lot area
 * AGC: [DEPRECATED] - This function is deprecated in favor of task-based inputs.
 * AGC: Inputs should now be defined in Admin Panel task templates and copied
 * AGC: to generated tasks via generateTasksForCrop() in lotServices.ts.
 * AGC: 
 * AGC: This function now returns an empty array as inputs are no longer
 * AGC: generated at the plan level. Use the inputs[] array on each Task instead.
 * 
 * @param cropType - The type of crop
 * @param planId - The plan ID to link inputs to
 * @param lotId - The lot ID
 * @param lotAreaHa - Lot area in hectares
 * @returns Empty array (inputs are now task-level, not plan-level)
 * @deprecated Use task.inputs[] from generateTasksForCrop() instead
 */
export const generateRequiredInputs = (
  cropType: CropType | string,
  planId: string,
  lotId: string,
  lotAreaHa: number
): RequiredInput[] => {
  console.warn(
    `⚠️ AGC: [DEPRECATED] generateRequiredInputs() is deprecated. ` +
    `Inputs for ${cropType} should come from Admin Panel task templates. ` +
    `Use task.inputs[] from generateTasksForCrop() instead.`
  )
  
  // AGC: Return empty array - inputs are now generated at task level
  // AGC: See lotServices.ts → generateTasksForCrop() for the new flow
  return []
}

/**
 * AGC: [CROP_PLAN] Create a new crop plan with required inputs (local generation)
 * AGC: This creates the plan object and generates all required inputs
 * @param request - Plan generation request
 * @param userId - User ID
 * @returns Generated crop plan with inputs
 */
export const createCropPlanLocal = (
  request: GenerateCropPlanRequest,
  userId: string
): { plan: CropPlan; requiredInputs: RequiredInput[] } => {
  console.log(`🌱 AGC: [CROP_PLAN] Creating crop plan for lot ${request.lotId}`)
  
  const now = getTimestamp()
  const planId = generateId('plan')
  
  // AGC: Calculate expected harvest date based on crop cycle
  const plantingDate = new Date(request.plannedPlantingDate)
  const cycleDays = getCropCycleDays(request.cropType)
  const harvestDate = new Date(plantingDate)
  harvestDate.setDate(harvestDate.getDate() + cycleDays)
  
  // AGC: Generate required inputs
  const requiredInputs = generateRequiredInputs(
    request.cropType,
    planId,
    request.lotId,
    request.lotAreaHa
  )
  
  // AGC: Calculate projected total yield
  const projectedYieldPerHa = request.projectedYieldPerHa || getDefaultYieldForCrop(request.cropType)
  const projectedTotalYield = Math.round(projectedYieldPerHa * request.lotAreaHa * 100) / 100
  
  // AGC: Create the plan object
  const plan: CropPlan = {
    id: planId,
    lotId: request.lotId,
    fieldId: request.fieldId,
    userId,
    cropType: request.cropType,
    cropName: request.cropName,
    variety: request.variety,
    plannedPlantingDate: request.plannedPlantingDate,
    expectedHarvestDate: harvestDate.toISOString().split('T')[0],
    cycleDays,
    status: 'draft',
    source: request.source || 'manual',
    recommendationId: request.recommendationId,
    projectedYieldPerHa,
    projectedTotalYield,
    lotAreaHa: request.lotAreaHa,
    taskIds: [], // AGC: Will be populated when tasks are created
    requiredInputIds: requiredInputs.map(i => i.id),
    totalInputsCount: requiredInputs.length,
    totalTasksCount: 0, // AGC: Will be updated when tasks are created
    notes: request.notes,
    createdAt: now,
    updatedAt: now,
  }
  
  console.log(`✅ AGC: [CROP_PLAN] Created plan ${planId} with ${requiredInputs.length} inputs`)
  
  return { plan, requiredInputs }
}

/**
 * AGC: [CROP_PLAN] Get crop cycle days
 */
const getCropCycleDays = (cropType: CropType | string): number => {
  const cycleDays: Record<string, number> = {
    soja: 120,
    milho: 140,
    milho_safrinha: 120,
    trigo: 130,
    feijao: 90,
    feijao_carioca: 90,
    feijao_preto: 90,
    algodao: 180,
    arroz: 120,
    sorgo: 110,
    aveia: 100,
    cana: 365,
    cafe: 365,
  }
  return cycleDays[cropType] || 120
}

/**
 * AGC: [CROP_PLAN] Get default yield for crop type (tn/ha)
 */
const getDefaultYieldForCrop = (cropType: CropType | string): number => {
  const yields: Record<string, number> = {
    soja: 3.2,
    milho: 5.5,
    milho_safrinha: 4.0,
    trigo: 3.0,
    feijao: 2.5,
    feijao_carioca: 2.5,
    feijao_preto: 2.5,
    algodao: 4.5,
    arroz: 4.0,
    sorgo: 3.5,
    cana: 80.0,
    cafe: 2.0,
  }
  return yields[cropType] || 3.0
}

// =============================================================================
// AGC: [CROP_PLAN] SUMMARY FUNCTIONS
// =============================================================================

/**
 * AGC: [CROP_PLAN] Create summary of required inputs organized by category
 */
export const createInputsSummary = (
  requiredInputs: RequiredInput[],
  planId: string,
  lotId: string,
  lotName: string,
  cropType: CropType | string,
  cropName: string,
  lotAreaHa: number
): RequiredInputsSummary => {
  // AGC: Group inputs by category
  const categoryMap = new Map<string, RequiredInput[]>()
  
  for (const input of requiredInputs) {
    const category = input.category
    if (!categoryMap.has(category)) {
      categoryMap.set(category, [])
    }
    categoryMap.get(category)!.push(input)
  }
  
  // AGC: Create category summaries
  const byCategory: InputCategorySummary[] = []
  
  categoryMap.forEach((inputs, category) => {
    const categoryInfo = getInputCategoryInfo(category as any)
    
    // AGC: Try to sum quantities if same unit
    const units = new Set(inputs.map(i => i.unit))
    let totalQuantity = 0
    let commonUnit = inputs[0]?.unit || 'unidades/ha'
    
    if (units.size === 1) {
      totalQuantity = inputs.reduce((sum, i) => sum + i.totalQuantity, 0)
      commonUnit = inputs[0].unit
    }
    
    byCategory.push({
      category: category as any,
      categoryName: categoryInfo.namePt,
      totalItems: inputs.length,
      totalQuantity: Math.round(totalQuantity * 100) / 100,
      unit: commonUnit,
      items: inputs.sort((a, b) => a.order - b.order),
    })
  })
  
  // AGC: Sort categories by typical order of application
  const categoryOrder = ['semente', 'inoculante', 'fungicida', 'inseticida', 'fertilizante', 'herbicida', 'adjuvante', 'regulador', 'corretivo', 'outro']
  byCategory.sort((a, b) => {
    const orderA = categoryOrder.indexOf(a.category)
    const orderB = categoryOrder.indexOf(b.category)
    return orderA - orderB
  })
  
  // AGC: Group by task (if taskId is present)
  const taskMap = new Map<string, { taskId: string; taskTitle: string; inputs: RequiredInput[] }>()
  const planLevelInputs: RequiredInput[] = []
  
  for (const input of requiredInputs) {
    if (input.taskId) {
      if (!taskMap.has(input.taskId)) {
        taskMap.set(input.taskId, {
          taskId: input.taskId,
          taskTitle: input.applicationPhase || 'Task',
          inputs: [],
        })
      }
      taskMap.get(input.taskId)!.inputs.push(input)
    } else {
      planLevelInputs.push(input)
    }
  }
  
  return {
    planId,
    lotId,
    lotName,
    cropType: cropType as CropType,
    cropName,
    lotAreaHa,
    byCategory,
    byTask: Array.from(taskMap.values()),
    planLevelInputs,
    totalInputsCount: requiredInputs.length,
    totalCategories: byCategory.length,
    generatedAt: getTimestamp(),
  }
}

// =============================================================================
// AGC: [CROP_PLAN] API FUNCTIONS
// =============================================================================

/**
 * AGC: [CROP_PLAN] Save crop plan to backend
 */
export const saveCropPlan = async (
  plan: CropPlan,
  requiredInputs: RequiredInput[],
  authToken?: string | null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/crop-plans`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ plan, requiredInputs }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to save crop plan: ${response.statusText}`)
    }

    const data = await response.json()
    console.log('✅ AGC: [CROP_PLAN] Crop plan saved to backend')
    return { success: true }
  } catch (error) {
    console.error('❌ AGC: [CROP_PLAN] Error saving crop plan:', error)
    return { success: false, error: (error as Error).message }
  }
}

/**
 * AGC: [CROP_PLAN] Get crop plan by ID
 */
export const getCropPlan = async (
  planId: string,
  authToken?: string | null
): Promise<CropPlan | null> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/crop-plans/${planId}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to get crop plan: ${response.statusText}`)
    }

    const data = await response.json()
    return data.plan
  } catch (error) {
    console.error('❌ AGC: [CROP_PLAN] Error getting crop plan:', error)
    return null
  }
}

/**
 * AGC: [CROP_PLAN] Get active crop plan for a lot
 */
export const getActiveCropPlanForLot = async (
  lotId: string,
  authToken?: string | null
): Promise<CropPlan | null> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/crop-plans/lot/${lotId}/active`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Failed to get active crop plan: ${response.statusText}`)
    }

    const data = await response.json()
    return data.plan
  } catch (error) {
    console.error('❌ AGC: [CROP_PLAN] Error getting active crop plan:', error)
    return null
  }
}

/**
 * AGC: [CROP_PLAN] Get required inputs for a lot/plan
 */
export const getRequiredInputs = async (
  request: GetRequiredInputsRequest,
  authToken?: string | null
): Promise<GetRequiredInputsResponse> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const params = new URLSearchParams()
    if (request.planId) params.append('planId', request.planId)
    if (request.includeCompletedTasks) params.append('includeCompletedTasks', 'true')

    const response = await fetch(`${BASE_URL}/api/v1/crop-plans/lot/${request.lotId}/inputs?${params}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, message: 'No active crop plan found for this lot' }
      }
      throw new Error(`Failed to get required inputs: ${response.statusText}`)
    }

    const data = await response.json()
    return { success: true, data: data.summary }
  } catch (error) {
    console.error('❌ AGC: [CROP_PLAN] Error getting required inputs:', error)
    return { success: false, error: (error as Error).message }
  }
}

/**
 * AGC: [CROP_PLAN] Update crop plan status
 */
export const updateCropPlanStatus = async (
  planId: string,
  status: CropPlanStatus,
  authToken?: string | null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/crop-plans/${planId}/status`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ status }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to update status: ${response.statusText}`)
    }

    console.log(`✅ AGC: [CROP_PLAN] Plan ${planId} status updated to ${status}`)
    return { success: true }
  } catch (error) {
    console.error('❌ AGC: [CROP_PLAN] Error updating plan status:', error)
    return { success: false, error: (error as Error).message }
  }
}

/**
 * AGC: [CROP_PLAN] Delete crop plan
 */
export const deleteCropPlan = async (
  planId: string,
  authToken?: string | null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/crop-plans/${planId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to delete crop plan: ${response.statusText}`)
    }

    console.log(`✅ AGC: [CROP_PLAN] Plan ${planId} deleted`)
    return { success: true }
  } catch (error) {
    console.error('❌ AGC: [CROP_PLAN] Error deleting crop plan:', error)
    return { success: false, error: (error as Error).message }
  }
}

// =============================================================================
// AGC: [CROP_PLAN] EXPORTS
// =============================================================================

export default {
  generateRequiredInputs,
  createCropPlanLocal,
  createInputsSummary,
  saveCropPlan,
  getCropPlan,
  getActiveCropPlanForLot,
  getRequiredInputs,
  updateCropPlanStatus,
  deleteCropPlan,
}




